import { procoreClient } from "./client";

type Uuid = string;

export type BimLevel = {
    elevation: number;
    id: Uuid;
    location_id: Uuid;
};

export type DrawingGroupItem = {
    bim_file_revision_instance_id: Uuid;
    file_info: {
        bim_file: {
            file_name: string;
        };
        bim_file_revision: {
            download_url: string;
        };
    };
    id: Uuid;
    /**
     * Platform scene-file placement id — keys the tiled mini map route's
     * signed-download-URL call (BM-13597, RFD00143 option C). Stored by
     * Model Manager; pending exposure in `plans/extended`.
     */
    platform_file_instance_id?: Uuid;
    platform_file_status: string;
    scene_version_status: string;
};

export type DrawingMapping = {
    id: Uuid;
    bim_file_revision_instance_id: Uuid;
    point_a: { x: number; y: number };
    point_b: { x: number; y: number };
    sheet_dimensions?: { width: number; height: number };
    use_for_2d_nav?: boolean;
};

export type ModelMapping = {
    mapping: {
        id: Uuid;
        point_a: { x: number; y: number; z: number };
        point_b: { x: number; y: number; z: number };
    };
};

export type SheetMappingItem = {
    id: Uuid;
    bim_file_revision_instance_id: Uuid;
    bim_level?: BimLevel | null;
    model_mapping?: {
        point_a: { x: number; y: number; z: number };
        point_b: { x: number; y: number; z: number };
    } | null;
    sheet_mapping?: {
        point_a: { x: number; y: number };
        point_b: { x: number; y: number };
        sheet_dimensions?: { width: number; height: number };
    } | null;
    use_for_2d_nav?: boolean;
};

export type BimPlan = {
    id: Uuid;
    name: string;
    drawing_group?: {
        drawing_group_items?: DrawingGroupItem[] | null;
    } | null;

    bim_level?: BimLevel | null;
    drawing_mappings?: DrawingMapping[] | null;
    model_mapping?: ModelMapping | null;

    sheet_mappings?: SheetMappingItem[] | null;

    use_for_2d_nav?: boolean;
};

type BimPlansData = {
    plans: BimPlan[];
};

export async function getBimPlans(params: {
    companyId: number;
    projectId: number;
    modelRevisionId: Uuid;
    /**
     * The open Snapshot's slice (`InitOptions.procore.sceneSliceId`), or absent
     * for the live federated model.
     *
     * Model Manager filters server-side to the sheets whose document container
     * survived into the slice (bim-model-manager-service#462), rather than the
     * viewer diffing against the slice itself: the 2D Sheets panel sends the
     * same parameter to the same filter, which is what keeps the two lists
     * identical for a given Snapshot (BM-14175, BM-14155).
     */
    sliceId?: Uuid;
}): Promise<BimPlan[]> {
    const query = new URLSearchParams({ include_deleted_sheets: "false" });
    if (params.sliceId) {
        query.set("slice_id", params.sliceId);
    }

    const response = await procoreClient<{ data: BimPlansData }>(
        `/rest/v2.0/companies/${params.companyId}/projects/${params.projectId}/bim/model_revisions/${params.modelRevisionId}/plans/extended?${query}`,
        {
            method: "GET",
        },
    );

    return response.data.plans;
}
